/**
 * MarketConditionUsedInPopover Component
 *
 * Displays "X criteria · Y confluences" count for a Market Condition Category.
 * Click/tap opens a popover showing labeled lists of criteria and confluence names.
 * Non-clickable when both counts are zero.
 */

import React from 'react';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
} from '@enfomentis/ui';

export interface MarketConditionUsedInPopoverProps {
  /** Array of criteria names that use this category */
  criteriaNames: string[];
  /** Array of confluence names that use this category */
  confluenceNames: string[];
}

export const MarketConditionUsedInPopover: React.FC<MarketConditionUsedInPopoverProps> = ({
  criteriaNames,
  confluenceNames,
}) => {
  const criteriaCount = criteriaNames.length;
  const confluenceCount = confluenceNames.length;
  const totalCount = criteriaCount + confluenceCount;

  // Format the display text
  const displayText = `${criteriaCount} ${criteriaCount === 1 ? 'criterion' : 'criteria'} · ${confluenceCount} ${confluenceCount === 1 ? 'confluence' : 'confluences'}`;

  // Empty state - no items use this category
  if (totalCount === 0) {
    return (
      <span className="text-sm text-text-tertiary">
        {displayText}
      </span>
    );
  }

  // Sort names alphabetically (case-insensitive) for display
  const sortedCriteria = [...criteriaNames].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
  const sortedConfluences = [...confluenceNames].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );

  return (
    <Popover isNonModal placement="bottom">
      <PopoverTrigger>
        <Button
          variant="light"
          size="sm"
          className="text-sm text-text-secondary hover:text-text-primary min-w-0 px-1"
        >
          {displayText}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-3">
        <div className="w-72 space-y-1">
          {criteriaCount > 0 && (
            <>
              <p className="text-xs font-medium text-text-secondary mb-2">
                Criteria using this category:
              </p>
              <ul className="space-y-1 max-h-48 overflow-y-auto min-w-0">
                {sortedCriteria.map((name, index) => (
                  <li
                    key={index}
                    className="text-sm text-text-primary truncate w-full"
                    title={name}
                  >
                    {name}
                  </li>
                ))}
              </ul>
            </>
          )}
          {confluenceCount > 0 && (
            <div className={criteriaCount > 0 ? 'mt-2 pt-2 border-t border-default-200' : ''}>
              <p className="text-xs font-medium text-text-secondary mb-2">
                Confluences using this category:
              </p>
              <ul className="space-y-1 max-h-48 overflow-y-auto min-w-0">
                {sortedConfluences.map((name, index) => (
                  <li
                    key={index}
                    className="text-sm text-text-primary truncate w-full"
                    title={name}
                  >
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
